import React from "react";
import {
  RouterProps,
  ActionList,
  ActionsFooter,
  IIconTextLinkProps,
  LogoImage,
  NameBar,
  ContactPerformerPopUp,
  SpotlightLogoGrayscaleSvg,
} from "component-library";
import { UserTypes, IExternalMedia, PremiumEntitlements } from "shared-types";
import {
  UserAttributes,
  getUserAttribute,
  checkIsSearchOnlyCD,
  doesUserHaveEntitlements,
} from "shared-auth";
import { dataTestIds } from "data-testids";
import {
  ABOUT_ME_FULL_PATH,
  AUDIO_FULL_PATH,
  BASIC_DETAILS_FULL_PATH,
  CREDITS_FULL_PATH,
  EDIT_PHOTO_FULL_PATH,
  PROFILE_CONTACT_FULL_PATH,
  SKILLS_FULL_PATH,
  SOCIAL_LINKS_FULL_PATH,
  TRAINING_FULL_PATH,
  VIDEO_FULL_PATH,
} from "app/config/routes";
import { config } from "config";
import { UserClaimTypes, UserManager, UserRoleManager, UserRoles } from "core";
import { MediaAsset } from "mediaModules/media/models";
import { IGlobalContextProps } from "shared/contexts/GlobalContext";
import { scrollToElement } from "shared/utils/dom";

import { GlobalEventTypes } from "contexts/GlobalContext";

import {
  AboutMe,
  BasicInfo,
  CreditsList,
  FacetCategory,
  IAgencyList,
  ICastingNotes,
  IEmail,
  INewShortlist,
  IShortlistPerformer,
  IShortlists,
  Photo,
  Skill,
  Training,
} from "models";
import { SocialMedia } from "models/Social";

import { IMainPhotoContent } from "components/common/MainPhoto";
import { IPopupProps } from "components/common/PopupPlaceholder";

import { parseQuery, parseToUrlQuery } from "shared/utils/http";
import {
  AboutMeSection,
  IAboutMeSectionContent,
} from "./components/AboutMeSection";
import {
  ActionsFooterRow,
  IActionsFooterRowContent,
} from "./components/ActionsFooterRow";
import { IAgenciesSectionContent } from "./components/AgenciesSection";
import { IBasicInfoSectionContent } from "./components/BasicInfoSection";
import {
  CreditsSection,
  ICreditsSectionContent,
} from "./components/CreditsSection";
import {
  FullScreenGallery,
  IFullScreenGalleryContent,
} from "./components/FullScreenGallery";
import {
  GalleryPhotoSection,
  IGalleryPhotoSectionContent,
} from "./components/GalleryPhotoSection";
import { IMediaSectionContent, MediaSection } from "./components/MediaSection";
import { IPaperCVContent, PaperCV } from "./components/PaperCV";
import {
  IShareViaEmailContent,
  ShareViaEmail,
} from "./components/ShareViaEmail";
import {
  ISkillsSectionContent,
  SkillsSection,
} from "./components/SkillsSection";
import { ISocialMediaListContent } from "./components/SocialMediaList";
import {
  ITrainingSectionContent,
  TrainingSection,
} from "./components/TrainingSection";

import { AvailabilityCalendar } from "./components/AvailabilityCalendar";
import {
  availabilityCalendarFallback,
  profileFallbackTranslations,
} from "../../store/fallbackTranslations";
import { agentContactValidationSchema } from "app/helpers/agent-contact-validation-schema";
import { TRACKING_EVENT_TYPE, pushToDataLayer } from "app/helpers/tracking";
import withFilmmakersAccessRestriction from "app/hoc/withFilmmakersAccessRestriction";
import {
  heapTrackingForMainPageEmailProfile,
  heapTrackingForMainPagePrintProfile,
} from "app/helpers/heapTracking";
import "./MainPage.scss";

const GALLERY_PHOTO_POPUP_SETTING: Partial<IPopupProps> = {
  isTransparent: true,
  hideCloseButton: true,
  width: { lg: 12, md: 12 },
};

const EMAIL_POPUP_SETTINGS: Partial<IPopupProps> = {
  width: { lg: 6, md: 8 },
  priority: "medium",
};
const AVAILABILITY_CALENDAR_POPUP_SETTINGS: Partial<IPopupProps> = {
  width: { lg: 8, md: 8 },
};

export interface IGallerySectionContent
  extends IFullScreenGalleryContent, IMainPhotoContent {
  noPhotoPlaceholder: string;
  hiddenPhotoText: string;
  galleryPhotoSectionDescription: string;
  galleryRowDescription: string;
  galleryPhotoDescription: string;
}

export interface IAccessibilityContent {
  editLinkAltText: string;
  nextButtonAltText: string;
  prevButtonAltText: string;
}

export interface IMainPageContent {
  socialMediaSection: ISocialMediaListContent;
  gallerySection: IGallerySectionContent;
  mediaSection: IMediaSectionContent;
  creditsSection: ICreditsSectionContent;
  basicInfoSection: IBasicInfoSectionContent;
  agenciesSection: IAgenciesSectionContent;
  skillsSection: ISkillsSectionContent;
  furtherInformationSection: ITrainingSectionContent & IAboutMeSectionContent;
  castingNotes: IActionsFooterRowContent;
  emailForm: IShareViaEmailContent;
  nameBarActionViewPublished: string;
  nameBarActionEmail: string;
  nameBarActionPrintProfile: string;
  nameBarActionMoreActions: string;
  accessibility: IAccessibilityContent;
}

export const IMainPageContentKeys = [
  "socialMediaSection",
  "gallerySection",
  "mediaSection",
  "creditsSection",
  "basicInfoSection",
  "agenciesSection",
  "skillsSection",
  "furtherInformationSection",
  "castingNotes",
  "emailForm",
  "nameBarActionViewPublished",
  "nameBarActionEmail",
  "nameBarActionPrintProfile",
  "nameBarActionMoreActions",
  "accessibility",
];

export interface IMainPageProps extends IGlobalContextProps, RouterProps {
  setDataLoadedCallback: (callback: () => void) => void;
  saveNotes: (notes: ICastingNotes) => void;
  initShortlistLoading: () => void;
  addShortList: (model: INewShortlist) => Promise<string>;
  addPerformerShortList: (
    shortlistId: number,
    model: IShortlistPerformer
  ) => Promise<string>;
  loadGalleryPhoto: (photo: Photo) => Promise<Photo | undefined>;
  sendEmail: (email: IEmail) => Promise<void>;
  isViewMode: boolean;
  content: IMainPageContent;
  cvContent: IPaperCVContent;
  mainPhoto?: Photo;
  galleryPhoto?: Photo[];
  creditsList?: CreditsList;
  skills?: Skill[];
  basicInfo?: BasicInfo;
  facets: FacetCategory[];
  aboutMe?: AboutMe;
  training?: Training;
  agencies?: IAgencyList;
  socialMedia?: SocialMedia;
  mediaAssets: MediaAsset[];
  mediaObjectPromiseMap: Map<string, Promise<MediaAsset>>;
  shortlists?: IShortlists;
  castingNotes?: ICastingNotes;
  userType: UserTypes;
  userTypeLabel: string;
  isSelfRepresented?: boolean;
  isLookingForRepresentation?: boolean;
  externalMediaItems?: IExternalMedia[];
  isInFilmmakers?: boolean;
}

interface IMainPageState {
  isPrintModeAvailable: boolean;
  isToastVisible: boolean;
  isContactPerformerPopUpOpen: boolean;
  performerName: string;
  senderName: string;
  senderEmail: string;
}

export const TYPE_AVAILABLE_FOR = [
  UserTypes.femaleGraduates,
  UserTypes.maleGraduates,
  UserTypes.youngPerformerFemale,
  UserTypes.youngPerformerMale,
  UserTypes.filmmakersProfile,
];

const submitFormEdnpointsMap = {
  [UserRoles.agent]: "lookingforrepresentation",
  [UserRoles.castingDirector]: "selfrepresented",
};

const getBrandLogo = (userType: UserTypes) => {
  if (userType === UserTypes.filmmakersProfile) {
    return LogoImage.Filmmakers;
  }

  return null;
};

export class MainPage extends React.Component<IMainPageProps, IMainPageState> {
  public readonly state: Readonly<IMainPageState> = {
    isPrintModeAvailable: false,
    isToastVisible: false,
    isContactPerformerPopUpOpen: false,
    performerName: UserManager.getClaimWithDefault(
      UserClaimTypes.profileFullName
    ),
    senderName: getUserAttribute(UserAttributes.FullName),
    senderEmail: getUserAttribute(UserAttributes.Email),
  };

  private defaultPageName: string;

  constructor(props: IMainPageProps) {
    super(props);

    this.defaultPageName = document.title;
  }

  public componentDidMount() {
    window.addEventListener("beforeprint", this.setPrintPageName);
    window.addEventListener("afterprint", this.setDefaultPageName);

    this.props.setDataLoadedCallback(this.handleDataLoading);
    this.checkPrintPage();
    this.checkEmailQueryAction();
  }

  public componentWillUnmount() {
    window.removeEventListener("beforeprint", this.setPrintPageName);
    window.removeEventListener("afterprint", this.setDefaultPageName);

    this.props.globalContext.removeListener(
      GlobalEventTypes.shareProfileViaEmail,
      this.openEmailingPopup
    );
    this.props.globalContext.removeListener(
      GlobalEventTypes.performerContact,
      this.openPerformerContactPopup
    );
    this.props.globalContext.removeListener(
      GlobalEventTypes.runProfilePrinting,
      this.printPage
    );
  }

  public render() {
    const { isPrintModeAvailable } = this.state;
    const isCastingDirector = UserRoleManager.isInRole(
      UserRoles.castingDirector
    );

    const isCastingDirectorSearchOnly = checkIsSearchOnlyCD();

    return (
      <div className="p-main-page">
        {this.renderContent(isCastingDirector)}
        {this.renderLogoIcons()}
        {isPrintModeAvailable && this.renderPrintContent()}
        {(isCastingDirector || isCastingDirectorSearchOnly) &&
          this.renderActionsFooter()}
      </div>
    );
  }

  private renderContent = (isCastingDirector: boolean) => {
    const {
      mainPhoto,
      galleryPhoto,
      content,
      basicInfo,
      facets,
      socialMedia,
      agencies,
      isViewMode,
      isSelfRepresented,
      isLookingForRepresentation,
      userType,
      isInFilmmakers,
    } = this.props;
    const {
      isContactPerformerPopUpOpen,
      performerName,
      senderName,
      senderEmail,
    } = this.state;

    const userRole = UserRoleManager.getUserRole() ?? "";
    const isAgent = UserRoleManager.isInRole(UserRoles.agent);

    const gallerySectionContent: IGalleryPhotoSectionContent = {
      mainPhotoContent: content.gallerySection,
      basicInfoSectionContent: content.basicInfoSection,
      agenciesSectionContent: content.agenciesSection,
      socialMediaSectionContent: content.socialMediaSection,
      noPhotoPlaceholder: content.gallerySection.noPhotoPlaceholder,
      hiddenPhotoText: content.gallerySection.hiddenPhotoText,
      editGalleryText: content.accessibility.editLinkAltText,
      nextButtonText: content.accessibility.nextButtonAltText,
      prevButtonText: content.accessibility.prevButtonAltText,
      galleryPhotoSectionDescription:
        content.gallerySection.galleryPhotoSectionDescription,
      galleryRowDescription: content.gallerySection.galleryRowDescription,
      galleryPhotoDescription: content.gallerySection.galleryPhotoDescription,
    };

    const performerContact = (event: React.MouseEvent) => {
      event.preventDefault();
      this.openPerformerContactPopup();
    };

    return (
      <div className="p-main-page__page">
        {this.renderNameBar()}
        <div className="p-main-page__gallery-bg">
          <div className="g-content-area p-main-page__gallery-content">
            <div className="p-main-page__gallery-photo-section g-col-lg-12">
              <GalleryPhotoSection
                mainPhoto={mainPhoto}
                galleryPhotos={galleryPhoto}
                editGallery={this.editGallery}
                openPhoto={this.openPhoto}
                mainPhotoError={false}
                basicInfo={basicInfo}
                basicInfoFacets={facets}
                editBasicInfo={this.editBasicInfo}
                agencies={agencies}
                editAgencies={this.editAgencies}
                editSocialMedia={this.editSocialMedia}
                socialMedia={socialMedia}
                isReadonly={isViewMode}
                content={gallerySectionContent}
                isSelfRepresented={isSelfRepresented}
                isLookingForRepresentation={isLookingForRepresentation}
                openPerformerContactPopup={performerContact}
                isToastVisible={this.state.isToastVisible}
                closeToast={this.closeToast}
                isFilmmakersProfile={userType === UserTypes.filmmakersProfile}
                isSLProfileInFilmmakers={isInFilmmakers}
                userType={userType}
              />
            </div>
          </div>
        </div>
        {this.renderMediaSection(isViewMode)}
        {this.renderAboutMeSection(isViewMode)}
        {this.renderCreditsSection(isViewMode)}
        {this.renderSkillsSection(isViewMode)}
        {this.renderFurtherInformationSection(isViewMode)}
        <ContactPerformerPopUp
          closeForm={() =>
            this.setState({ isContactPerformerPopUpOpen: false })
          }
          performerName={performerName}
          senderName={senderName}
          senderEmail={senderEmail}
          isOpen={isContactPerformerPopUpOpen}
          performerId={UserManager.userId}
          canAttachFiles={isCastingDirector}
          emailSubjectLine={
            isAgent
              ? profileFallbackTranslations.contactPerformerAgentEmailSubject
              : undefined
          }
          submitformEndpoint={submitFormEdnpointsMap[userRole]}
          customValidationSchema={agentContactValidationSchema}
          successCallback={this.handleContactPerformerSuccess}
        />
      </div>
    );
  };

  private renderLogoIcons = () => (
    <div id="pre-loaded-print-icons">
      <img src={SpotlightLogoGrayscaleSvg} alt="Spotlight Logo" />
    </div>
  );

  private closeToast = () => {
    this.setState({ isToastVisible: false });
  };

  private renderPrintContent = () => {
    let { galleryPhoto } = this.props;
    const {
      userType,
      userTypeLabel,
      mainPhoto,
      socialMedia,
      creditsList,
      skills,
      basicInfo,
      facets,
      aboutMe,
      training,
      agencies,
      cvContent,
      castingNotes = { content: "" },
      isViewMode,
    } = this.props;

    const cvNotes =
      UserRoleManager.isInRole(UserRoles.castingDirector) ||
      checkIsSearchOnlyCD()
        ? castingNotes.content
        : undefined;

    if (galleryPhoto) {
      galleryPhoto = galleryPhoto.filter((photo) => !photo.isHidden);
    }

    return (
      <PaperCV
        cvNotes={cvNotes}
        fullName={UserManager.getClaimWithDefault(
          UserClaimTypes.profileFullName
        )}
        userType={userType}
        userTypeLabel={userTypeLabel}
        mainPhoto={mainPhoto}
        socialMedia={socialMedia}
        galleryPhoto={galleryPhoto}
        creditsList={creditsList}
        skills={skills}
        content={cvContent}
        basicInfo={basicInfo}
        facets={facets}
        aboutMe={aboutMe}
        training={training}
        agencies={agencies}
        isViewMode={isViewMode}
      />
    );
  };

  private renderActionsFooter = () => {
    return (
      <ActionsFooter
        renderControl={this.renderActionsFooterRow}
        variant="primary"
        isSticky
        fullWidth={true}
      />
    );
  };

  private renderActionsFooterRow = () => {
    const {
      content,
      agencies,
      castingNotes,
      saveNotes,
      shortlists,
      initShortlistLoading,
      addShortList,
      addPerformerShortList,
    } = this.props;

    return (
      <ActionsFooterRow
        content={content.castingNotes}
        shortlists={shortlists}
        agencies={agencies}
        notes={castingNotes}
        initShortlistLoading={initShortlistLoading}
        printPage={() => {
          heapTrackingForMainPagePrintProfile();

          this.printPage();
        }}
        sendEmail={() => {
          heapTrackingForMainPageEmailProfile();

          this.openEmailingPopup();
        }}
        saveNotes={saveNotes}
        addShortList={addShortList}
        addPerformerShortList={addPerformerShortList}
      />
    );
  };

  private renderNameBar = () => {
    const { userTypeLabel, isViewMode, userType, basicInfo } = this.props;
    const isAgent = UserRoleManager.isInRole(UserRoles.agent);
    const fullName = UserManager.getClaimWithDefault(
      UserClaimTypes.profileFullName
    );
    const isTypeVisible = TYPE_AVAILABLE_FOR.some((type) => type === userType);
    const visibleUserType = isTypeVisible ? userTypeLabel : "";
    const logoImage = getBrandLogo(userType);

    const actionList = !isViewMode ? this.renderNameBarActionList() : undefined;
    const performerHasPremiumCalendar =
      doesUserHaveEntitlements(PremiumEntitlements.Premium_Calendar) &&
      !isAgent;
    const agentAbleViewPremiumCalendar =
      isAgent && basicInfo?.hasPremiumCalendar;

    const secondaryActionList =
      !isViewMode &&
      (agentAbleViewPremiumCalendar || performerHasPremiumCalendar)
        ? this.createSecondaryActionList()
        : undefined;

    return (
      <NameBar
        className="p-main-page__name-bar"
        fullName={fullName}
        logo={logoImage}
        userType={visibleUserType}
        linkUrl=""
        actionSection={actionList}
        secondaryActionSection={secondaryActionList}
      />
    );
  };

  private createSecondaryActionList = () => {
    return (
      <ActionList
        items={[
          {
            text: availabilityCalendarFallback.buttonTitle,
            variant: "primary",
            iconName: "icon-performancecal",
            handleClick: this.openAvailabilityCalendarPopup,
          },
        ]}
        variant="nameBar"
      />
    );
  };

  private renderNameBarActionList = () => {
    const { content } = this.props;
    const { globalContext } = this.props;

    const profileViewLink = `${
      config.wwwFrontEndUrl
    }${UserManager.getClaimWithDefault(UserClaimTypes.viewPin)}`;

    const startPrint = (event: React.MouseEvent) => {
      event.preventDefault();

      heapTrackingForMainPagePrintProfile();

      globalContext.notifyListener(
        GlobalEventTypes.runProfilePrinting,
        {},
        true
      );
    };

    const shareViaEmail = (event: React.MouseEvent) => {
      event.preventDefault();

      heapTrackingForMainPageEmailProfile();

      this.openEmailingPopup();
    };

    const actionItems: IIconTextLinkProps[] = [
      {
        text: content.nameBarActionViewPublished,
        variant: "primary",
        iconName: "icon-clients",
        linkUrl: profileViewLink,
        target: "_blank",
      },
      {
        text: content.nameBarActionEmail,
        variant: "primary",
        iconName: "icon-messages",
        handleClick: shareViaEmail,
      },
      {
        text: content.nameBarActionPrintProfile,
        variant: "primary",
        iconName: "icon-Print",
        handleClick: startPrint,
      },
    ];

    return (
      <ActionList
        items={actionItems}
        moreButtonText={content.nameBarActionMoreActions}
        variant="nameBar"
      />
    );
  };

  private renderMediaSection = (isReadonly: boolean) => {
    const {
      content,
      mediaAssets,
      mediaObjectPromiseMap,
      externalMediaItems = [],
      userType,
    } = this.props;

    const isFilmmakers = userType === UserTypes.filmmakersProfile;

    const isVisible =
      ((mediaAssets.length > 0 ||
        (externalMediaItems?.length > 0 && isFilmmakers)) &&
        isReadonly) ||
      !isReadonly;

    return (
      isVisible &&
      content.mediaSection && (
        <div
          className="g-content-area p-main-page__media-content"
          data-testid={dataTestIds.profile["MainPage.MediaSection"]}
        >
          <div className="g-col-lg-12">
            <MediaSection
              content={{ ...content.mediaSection, ...content.accessibility }}
              mediaAssets={mediaAssets}
              externalMediaItems={externalMediaItems}
              mediaObjectPromiseMap={mediaObjectPromiseMap}
              editShowreels={this.editShowreels}
              editAudioClips={this.editAudioClips}
              openPopup={this.openPopup}
              closePopup={this.closePopup}
              isReadonly={isReadonly}
              isFilmmakers={isFilmmakers}
            />
          </div>
        </div>
      )
    );
  };

  private renderAboutMeSection = (isReadonly: boolean) => {
    const { content, aboutMe } = this.props;

    const isVisible =
      (aboutMe && aboutMe.currentAppearance && isReadonly) || !isReadonly;

    return (
      isVisible && (
        <div className="g-content-area p-main-page__content">
          <div className="p-main-page__about-me-section g-col-lg-12">
            <AboutMeSection
              isReadonly={isReadonly}
              content={{
                ...content.furtherInformationSection,
                ...content.accessibility,
              }}
              editAboutMe={this.editAboutMe}
              aboutMe={aboutMe}
            />
          </div>
        </div>
      )
    );
  };

  private renderCreditsSection = (isReadonly: boolean) => {
    const { content, creditsList } = this.props;

    const isVisible =
      (creditsList && creditsList.credits.length > 0 && isReadonly) ||
      !isReadonly;

    return (
      isVisible && (
        <div className="g-content-area p-main-page__content">
          <div className="p-main-page__credits-section g-col-lg-12">
            <CreditsSection
              editCredits={this.editCredits}
              creditsList={creditsList}
              content={{ ...content.creditsSection, ...content.accessibility }}
              isReadonly={isReadonly}
            />
          </div>
        </div>
      )
    );
  };

  private renderSkillsSection = (isReadonly: boolean) => {
    const { content, skills, facets } = this.props;

    const isVisible =
      (skills && skills.length > 0 && isReadonly) || !isReadonly;

    return (
      isVisible && (
        <div className="g-content-area p-main-page__content">
          <div className="p-main-page__skills-section g-col-lg-12">
            <SkillsSection
              content={{ ...content.skillsSection, ...content.accessibility }}
              skills={skills}
              isReadonly={isReadonly}
              skillFacets={facets}
              editSkills={this.editSkills}
            />
          </div>
        </div>
      )
    );
  };

  private renderFurtherInformationSection = (isReadonly: boolean) => {
    const { content, training } = this.props;

    const isVisible = (training && training.notes && isReadonly) || !isReadonly;

    return (
      isVisible && (
        <div className="g-content-area p-main-page__content">
          <div
            className="p-main-page__training-section g-col-lg-12"
            data-testid={dataTestIds.profile["MainPage.TrainingSection"]}
          >
            <TrainingSection
              isReadonly={isReadonly}
              content={{
                ...content.furtherInformationSection,
                ...content.accessibility,
              }}
              editTraining={this.editTraining}
              training={training}
            />
          </div>
        </div>
      )
    );
  };

  private openPhoto = (photoToOpen: Photo) => {
    const { loadGalleryPhoto } = this.props;
    const { mainPhoto, galleryPhoto = [], content, isViewMode } = this.props;

    const gallery = (
      [mainPhoto, ...galleryPhoto].filter(Boolean) as Photo[]
    ).filter((photo) => !isViewMode || !photo.isHidden);

    const photoDataRequests = new Map<string, Promise<Photo | undefined>>();

    gallery.forEach((photo) =>
      photoDataRequests.set(photo.id, loadGalleryPhoto(photo))
    );

    const galleryContent = (
      <FullScreenGallery
        photo={photoToOpen}
        content={{ ...content.gallerySection, ...content.accessibility }}
        galleryPhotos={gallery}
        closePhoto={this.closePopup}
        photoDataRequests={photoDataRequests}
      />
    );

    this.openPopup(galleryContent, GALLERY_PHOTO_POPUP_SETTING);
  };

  private editGallery = () => this.redirect(EDIT_PHOTO_FULL_PATH);

  private editShowreels = () => this.redirect(VIDEO_FULL_PATH);

  private editAudioClips = () => this.redirect(AUDIO_FULL_PATH);

  private editCredits = () => this.redirect(CREDITS_FULL_PATH);

  private editBasicInfo = () => this.redirect(BASIC_DETAILS_FULL_PATH);

  private editSkills = () => this.redirect(SKILLS_FULL_PATH);

  private editTraining = () => this.redirect(TRAINING_FULL_PATH);

  private editAboutMe = () => this.redirect(ABOUT_ME_FULL_PATH);

  private editAgencies = () => this.redirect(PROFILE_CONTACT_FULL_PATH);

  private editSocialMedia = () => this.redirect(SOCIAL_LINKS_FULL_PATH);

  private redirect = (url: string) => {
    scrollToElement(".a-master-page__hidden-panel", { block: "end" });
    this.props.router.navigate(url);
  };

  private openPopup = (
    content: React.ReactNode,
    popupProps: Partial<IPopupProps>
  ) =>
    this.props.globalContext.notifyListener(GlobalEventTypes.showPopup, {
      content,
      popupProps,
    });

  private closePopup = () =>
    this.props.globalContext.notifyListener(GlobalEventTypes.showPopup, {
      content: null,
    });

  private openEmailingPopup = () => {
    const {
      content: { emailForm },
      sendEmail,
    } = this.props;

    const shareViaEmailForm = (
      <ShareViaEmail
        content={emailForm}
        sendEmail={sendEmail}
        closeForm={this.closePopup}
      />
    );

    this.openPopup(shareViaEmailForm, EMAIL_POPUP_SETTINGS);
  };

  private openAvailabilityCalendarPopup = () => {
    const availabilityCalendar = (
      <AvailabilityCalendar closePopup={this.closePopup} />
    );
    this.openPopup(availabilityCalendar, AVAILABILITY_CALENDAR_POPUP_SETTINGS);
  };

  private openPerformerContactPopup = () =>
    this.setState({ isContactPerformerPopUpOpen: true });

  private checkPrintPage() {
    const {
      router: {
        location: { search },
      },
    } = this.props;
    const { action } = parseQuery(search);

    if (action === "print") {
      window.setTimeout(() => {
        this.printPage();
      }, 2000);
    }
  }

  private checkEmailQueryAction() {
    const {
      router: {
        location: { search, pathname },
        navigate,
      },
    } = this.props;
    const { action, ...queryObject } = parseQuery(search);

    if (action !== "email") return;

    this.props.globalContext.notifyListener(
      GlobalEventTypes.shareProfileViaEmail,
      {},
      true
    );
    navigate(pathname + parseToUrlQuery(queryObject), { replace: true });
  }

  private printPage = () => {
    this.setState({ isPrintModeAvailable: true }, () => {
      try {
        document.execCommand("print");
      } catch (error) {
        window.print();
      }
    });
  };

  private setPrintPageName = () => {
    document.title =
      "Spotlight: " +
      UserManager.getClaimWithDefault(UserClaimTypes.profileFullName);
    this.setState({ isPrintModeAvailable: true });
  };

  private setDefaultPageName = () => {
    document.title = this.defaultPageName;
    this.setState({ isPrintModeAvailable: false });
    const {
      router: {
        location: { search, pathname },
        navigate,
      },
    } = this.props;
    // eslint-disable-next-line
    const { action, ...queryObject } = parseQuery(search);
    navigate(pathname + parseToUrlQuery(queryObject), { replace: true });
  };

  private handleDataLoading = () => {
    this.props.globalContext.addListener(
      GlobalEventTypes.runProfilePrinting,
      this.printPage
    );
    this.props.globalContext.addListener(
      GlobalEventTypes.shareProfileViaEmail,
      this.openEmailingPopup
    );
  };

  private handleContactPerformerSuccess = () => {
    pushToDataLayer(TRACKING_EVENT_TYPE.SubmitContactPerformerForm, {
      memberType: UserRoleManager.getUserRole(),
      performerPortalId: UserManager.userId,
    });
  };
}

export default withFilmmakersAccessRestriction<IMainPageProps>(MainPage);
